import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, Crosshair } from "lucide-react";
import { IMAGES } from "../content";
import { scrollToId } from "./bits";

const Radar = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let angle = 0;
    const P = (a) => `rgba(4,120,87,${a})`;
    const tracks = [
      { a: 0.4, r: 0.32, id: "TRK-01", s: "diamond" },
      { a: 1.1, r: 0.72, id: "TRK-02", s: "square" },
      { a: 1.9, r: 0.55, id: "UAV-03", s: "circle" },
      { a: 2.6, r: 0.88, id: "TRK-04", s: "diamond" },
      { a: 3.2, r: 0.42, id: "UNK-05", s: "square" },
      { a: 3.9, r: 0.65, id: "TRK-06", s: "circle" },
      { a: 4.5, r: 0.24, id: "UAV-07", s: "diamond" },
      { a: 5.1, r: 0.78, id: "TRK-08", s: "square" },
      { a: 5.8, r: 0.5, id: "TRK-09", s: "circle" },
      { a: 0.8, r: 0.6, id: "UAV-10", s: "diamond" },
      { a: 1.6, r: 0.3, id: "TRK-11", s: "circle" },
      { a: 2.9, r: 0.7, id: "UNK-12", s: "square" },
      { a: 4.8, r: 0.45, id: "TRK-13", s: "diamond" },
      { a: 5.5, r: 0.9, id: "TRK-14", s: "circle" }
    ];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const drawScope = (cx, cy, maxR) => {
      // outer bezel rings
      ctx.strokeStyle = P(0.7);
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(cx, cy, maxR + 26, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = P(0.35);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(cx, cy, maxR + 16, 0, Math.PI * 2);
      ctx.stroke();
      // range rings
      for (let i = 1; i <= 4; i++) {
        ctx.strokeStyle = P(i === 4 ? 0.65 : 0.4);
        ctx.lineWidth = i === 4 ? 3 : 2;
        ctx.beginPath();
        ctx.arc(cx, cy, (maxR / 4) * i, 0, Math.PI * 2);
        ctx.stroke();
      }
      // azimuth ticks + cardinal labels
      ctx.font = "bold 16px 'JetBrains Mono', monospace";
      ctx.fillStyle = P(0.95);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      for (let d = 0; d < 360; d += 10) {
        const rad = ((d - 90) * Math.PI) / 180;
        const major = d % 30 === 0;
        const len = major ? 18 : 9;
        ctx.strokeStyle = P(major ? 0.85 : 0.45);
        ctx.lineWidth = major ? 3 : 2;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(rad) * maxR, cy + Math.sin(rad) * maxR);
        ctx.lineTo(cx + Math.cos(rad) * (maxR - len), cy + Math.sin(rad) * (maxR - len));
        ctx.stroke();
        if (d % 90 === 0) {
          ctx.fillText(
            String(d).padStart(3, "0"),
            cx + Math.cos(rad) * (maxR + 8),
            cy + Math.sin(rad) * (maxR + 8)
          );
        }
      }
      // crosshair
      ctx.strokeStyle = P(0.35);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(cx - maxR, cy);
      ctx.lineTo(cx + maxR, cy);
      ctx.moveTo(cx, cy - maxR);
      ctx.lineTo(cx, cy + maxR);
      ctx.stroke();
    };

    const draw = () => {
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      const cx = w * 0.82;
      const cy = h * 0.55;
      const maxR = Math.min(w, h) * 0.42;

      drawScope(cx, cy, maxR);

      // ghost trail + main sweep beam
      [angle - 0.55, angle].forEach((a, idx) => {
        const grad = ctx.createConicGradient ? ctx.createConicGradient(a, cx, cy) : null;
        if (grad) {
          grad.addColorStop(0, P(idx === 1 ? 0.65 : 0.25));
          grad.addColorStop(0.2, P(0));
          grad.addColorStop(1, P(0));
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.moveTo(cx, cy);
          ctx.arc(cx, cy, maxR, 0, Math.PI * 2);
          ctx.fill();
        }
      });

      // leading edge - thick beam
      ctx.strokeStyle = P(1);
      ctx.lineWidth = 6;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(angle) * maxR, cy + Math.sin(angle) * maxR);
      ctx.stroke();

      // target tracks: diamond markers, bright when scanned, with tags
      ctx.font = "bold 13px 'JetBrains Mono', monospace";
      ctx.textAlign = "left";
      tracks.forEach((b) => {
        let diff = (angle - b.a) % (Math.PI * 2);
        if (diff < 0) diff += Math.PI * 2;
        const alpha = Math.max(0.15, 1 - diff / 3.2);
        const bx = cx + Math.cos(b.a) * maxR * b.r;
        const by = cy + Math.sin(b.a) * maxR * b.r;
        ctx.strokeStyle = P(alpha);
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        if (b.s === "square") {
          ctx.rect(bx - 8, by - 8, 16, 16);
        } else if (b.s === "circle") {
          ctx.arc(bx, by, 8, 0, Math.PI * 2);
        } else {
          ctx.moveTo(bx, by - 9);
          ctx.lineTo(bx + 9, by);
          ctx.lineTo(bx, by + 9);
          ctx.lineTo(bx - 9, by);
          ctx.closePath();
        }
        ctx.stroke();
        if (alpha > 0.5) {
          ctx.fillStyle = P(Math.min(1, alpha + 0.2));
          ctx.fillText(b.id, bx + 14, by - 11);
        }
      });

      // center + azimuth readout
      ctx.fillStyle = P(1);
      ctx.beginPath();
      ctx.arc(cx, cy, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.font = "bold 14px 'JetBrains Mono', monospace";
      ctx.textAlign = "left";
      ctx.fillStyle = P(0.95);
      const az = Math.round(((angle * 180) / Math.PI + 90) % 360);
      ctx.fillText(`AZ ${String(az).padStart(3, "0")}°  RNG 40NM`, cx - maxR + 6, cy + maxR + 20);

      angle += 0.008;
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={ref} className="absolute inset-0 w-full h-full" aria-hidden />;
};

const Hero = ({ t }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden tactical-grid scanlines"
    >
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt=""
          className="w-full h-[120%] object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#E9EDF3]/70 via-[#E9EDF3]/55 to-[#E9EDF3]" />
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(ellipse 80% 50% at 50% -10%, rgba(4,120,87,0.12), transparent 70%)" }}
        />
      </motion.div>

      <div className="absolute inset-0 hidden md:block">
        <Radar />
      </div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-32 pb-20">
        <motion.p
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="font-mono-tech text-xs sm:text-base uppercase tracking-[0.3em] text-emerald/90 flex items-center gap-3 mb-8"
        >
          <Crosshair size={16} className="text-emerald" />
          {t.eyebrow}
        </motion.p>

        <h1 data-testid="hero-headline" className="font-display font-extrabold uppercase leading-[0.95] tracking-tight text-4xl sm:text-6xl lg:text-7xl">
          {t.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                className={`block ${i === t.accentLine ? "text-emerald text-glow" : "text-[#0B0D10]"}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtext"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0 }}
          className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-neutral-800"
        >
          {t.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.15 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-explore-capabilities"
            onClick={() => scrollToId("capabilities")}
            className="bg-emerald text-[#E9EDF3] font-display font-bold uppercase tracking-wider px-8 py-3.5 rounded-sm hover:bg-emerald-dim transition-colors duration-300"
          >
            {t.ctaPrimary}
          </button>
          <button
            data-testid="hero-cta-contact-form"
            onClick={() => scrollToId("contact")}
            className="border border-[#CBD3DF] text-neutral-900 font-display font-bold uppercase tracking-wider px-8 py-3.5 rounded-sm hover:border-emerald hover:text-emerald transition-colors duration-300"
          >
            {t.ctaSecondary}
          </button>
        </motion.div>

        <motion.div
          data-testid="hero-telemetry-status"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3"
        >
          {t.telemetry.map((item, i) => (
            <span key={i} className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-neutral-600 flex items-center gap-2">
              {i === 0 && <span className="status-dot inline-block w-2 h-2 rounded-full bg-emerald" />}
              {item}
            </span>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        onClick={() => scrollToId("capabilities")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-neutral-600 hover:text-emerald transition-colors"
        aria-label={t.scroll}
      >
        <span className="font-mono-tech text-[10px] tracking-[0.3em]">{t.scroll}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronDown size={18} />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
